import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'autonome.pageWidth'
/** The attribute `index.css` reads: `html[data-page-width="full"]` lifts every page's width cap. */
const ATTRIBUTE = 'data-page-width'

function storedFullWidth(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'full'
  } catch {
    return false
  }
}

/**
 * Whether this browser draws the app full width (the user's option, 27 October 2026): by default every page sits in
 * a centred column; switched on, the column is as wide as the window. A convenience kept per browser in localStorage
 * (never on the server), applied as an attribute on the document so one stylesheet rule reaches every page.
 */
export function usePageWidth(): { full: boolean; setFull: (full: boolean) => void } {
  const [full, setFullState] = useState<boolean>(() => (typeof window === 'undefined' ? false : storedFullWidth()))
  useEffect(() => {
    if (full) {
      document.documentElement.setAttribute(ATTRIBUTE, 'full')
    } else {
      document.documentElement.removeAttribute(ATTRIBUTE)
    }
  }, [full])
  const setFull = useCallback((next: boolean) => {
    setFullState(next)
    try {
      if (next) {
        window.localStorage.setItem(STORAGE_KEY, 'full')
      } else {
        window.localStorage.removeItem(STORAGE_KEY)
      }
    } catch {
      // Storage may be unavailable (a private window); the choice then lasts the page.
    }
  }, [])
  return { full, setFull }
}
